/**
 * Notification Provider Component
 * Manages global notification/toast state for the OC Widget
 */

import { Notification } from '@hl/ui-components';
import React, { useCallback, useContext, useState } from 'react';

export type NotificationType = 'success' | 'info' | 'inprogress' | 'error';

interface NotificationState {
  visible: boolean;
  type: NotificationType;
  message: string;
}

interface NotificationContextValue {
  showNotification: (type: NotificationType, message: string) => void;
}

const NotificationContext = React.createContext<NotificationContextValue | null>(null);
NotificationContext.displayName = 'NotificationContext';

/**
 * Hook to access notification context
 * Must be used within NotificationProvider
 */
export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider Component
 * Provides notification/toast functionality to the OC Widget
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notification, setNotification] = useState<NotificationState>({
    visible: false,
    type: 'success',
    message: '',
  });

  const showNotification = useCallback((type: NotificationType, message: string) => {
    setNotification({
      visible: true,
      type,
      message,
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <Notification
        visible={notification.visible}
        type={notification.type}
        message={notification.message}
        onHide={hideNotification}
        openTimeLength={3000}
      />
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;

